import { AttachmentOutcome, MailMessageStatus } from '../../api';

/**
 * The words the inbox screen uses, and the one thing it says loudest.
 *
 * A lookup module like [`submissionCycle`](./submissionCycle.ts), and here for
 * the same reason: a status appears on the queue line, in the message panel and
 * in the summary above both, and "Sem remetente" spelled three ways across them
 * is how a screen stops being trusted.
 *
 * The refusal reasons are the exception and are the server's own words, the way
 * the review queue's flag messages are. "O arquivo não é uma planilha legível"
 * is a sentence this module could produce, but "a planilha não tem a coluna
 * Salário" is not, and the second is the one that tells the customer what to
 * send instead.
 */

export const MAILBOX_COPY = {
  title: 'Caixa de entrada',
  description:
    'O que chegou por e-mail de clientes em migração, e o que foi feito com cada mensagem. ' +
    'Uma planilha vira uma prévia — nenhuma movimentação é criada por e-mail. ' +
    'Confirmar a prévia continua sendo uma decisão de alguém.',
  queueTitle: 'Mensagens',
  queueDescription:
    'Mais recentes primeiro. Toda mensagem tem uma linha, tenha dado em alguma coisa ou não.',
  onlyOutstanding: 'Mostrar apenas o que falta tratar',
  outstanding: 'Aguardando alguém',
  poll: 'Ler a caixa agora',
  polling: 'Lendo…',
  open: 'Abrir',
  retry: 'Reprocessar',
  discard: 'Descartar',
  discardReason: 'Por que esta mensagem não é nossa',
  discardHint: 'Fica registrado na mensagem e na trilha de auditoria.',
  messageTitle: 'Mensagem',
  attachments: 'Anexos',
  noMessages: 'Nenhuma mensagem foi lida desta caixa ainda.',
  noOutstanding: 'Nada aguardando. Toda mensagem lida já foi tratada.',
  noAttachments: 'A mensagem não trouxe nenhum arquivo.',
  sendersTitle: 'Remetentes autorizados',
  sendersDescription:
    'Um endereço, um contrato. Um endereço que não está aqui não é confiável: a mensagem é ' +
    'guardada como "sem remetente" e nunca associada a uma empresa pelos códigos escritos ' +
    'dentro da planilha.',
  noSenders: 'Nenhum endereço autorizado ainda.',
  revoke: 'Retirar autorização',
  attempt: 'Tentativa',
  previousAttempt: 'Ver a tentativa anterior',
  latestAttempt: 'Ver a tentativa mais recente',
  /**
   * Said on the screen and not only in the API documentation. An analyst who
   * believed a poll had filed movements would stop looking at the review queue,
   * which is the one place those movements are actually decided.
   */
  nothingIsApproved:
    'Nada aqui aprova nada. Uma planilha que chega por e-mail vira uma prévia, e as ' +
    'movimentações só existem quando alguém confirma essa prévia.',
} as const;

/** Where one message got to. */
export const MAIL_STATUS_LABELS: Record<MailMessageStatus, string> = {
  [MailMessageStatus.INGESTED]: 'Processada',
  [MailMessageStatus.UNROUTED]: 'Remetente desconhecido',
  [MailMessageStatus.REFUSED]: 'Nada aproveitável',
  [MailMessageStatus.DISCARDED]: 'Descartada',
};

/** What became of one attached file. */
export const ATTACHMENT_OUTCOME_LABELS: Record<AttachmentOutcome, string> = {
  [AttachmentOutcome.SPREADSHEET]: 'Prévia de planilha',
  [AttachmentOutcome.DOCUMENT]: 'Documento guardado',
  [AttachmentOutcome.IGNORED]: 'Parte da mensagem',
  [AttachmentOutcome.REJECTED]: 'Recusado',
};

/**
 * Why nothing could be made of a message, in pt-BR.
 *
 * Keyed by `refusal_code` rather than by the server's sentence, which is the
 * rule everywhere in this application: the code is stable and the prose is
 * written to be made clearer.
 */
export const REFUSAL_LABELS: Record<string, string> = {
  sender_not_registered: 'O endereço não está autorizado em nenhum contrato.',
  unreadable_message: 'A mensagem não pôde ser lida, ou não traz remetente.',
  no_usable_attachment: 'Nenhum anexo é planilha nem documento aceito.',
  empty_attachment: 'O anexo chegou vazio.',
  attachment_too_large: 'O anexo passa do tamanho máximo.',
  unreadable_attachment: 'O anexo não é uma planilha legível nem um documento aceito.',
};

/** The server's own sentence when we have one, and our label when we do not. */
export function refusalText(
  code: string | null | undefined,
  reason: string | null | undefined,
): string {
  if (reason != null && reason !== '') {
    return reason;
  }
  return code != null ? (REFUSAL_LABELS[code] ?? code) : '';
}

/**
 * Whether a message is still somebody's to deal with.
 *
 * The same rule the API's `outstanding` filter applies, restated here so a line
 * already on screen can be badged without going back for it.
 */
export function isOutstanding(status: MailMessageStatus): boolean {
  return status === MailMessageStatus.UNROUTED || status === MailMessageStatus.REFUSED;
}
